import { useEffect, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useFrequencyArrayAdjustment } from 'components/configured-service/EditServiceRecurrenceDialog/hooks/useFrequencyArrayAdjustment'
import { useIntervalTransition } from 'components/configured-service/EditServiceRecurrenceDialog/hooks/useIntervalTransition'
import { getUniqueRecurrenceOptions } from 'components/configured-service/EditServiceRecurrenceDialog/utils/recurrenceOptionsHelper'
import { shallowEqual, useSelector } from 'react-redux'
import { CREATE_DRAFT_CS_TABS, selectCSDCurrentTab } from 'slices/configuredService/createDraftCSDSlice'
import { defaultValues as createDraftDefaults } from '../../settings'

/**
 * Handles recurrence-related wiring for ScheduleTabDraft using the main form.
 * - Aligns arrays when frequency/interval changes
 * - Resets recurrence fields when priced service changes
 * - Exposes snapshot data for previews
 */
export function useScheduleRecurrenceDraft({ metaFiltersData }) {
  const { control, setValue } = useFormContext()
  const currentTab = useSelector(selectCSDCurrentTab, shallowEqual)
  const isScheduleTab = useMemo(() => currentTab === CREATE_DRAFT_CS_TABS.SCHEDULE, [currentTab])
  const selectedPricedService = useWatch({ control, name: 'selectedPricedService' })

  // Options for recurrence
  const recurrenceOptions = useMemo(() => getUniqueRecurrenceOptions(metaFiltersData), [metaFiltersData])

  // Subscribe to specific form fields
  const recurrenceFrequency = useWatch({ control, name: 'recurrenceFrequency' })
  const serviceInterval = useWatch({ control, name: 'serviceInterval' })
  const daysOfWeek = useWatch({ control, name: 'daysOfWeek' })
  const eventInstanceType = useWatch({ control, name: 'eventInstanceType' })
  const eventOccurrence = useWatch({ control, name: 'eventOccurrence' })
  const dayOfTheWeek = useWatch({ control, name: 'dayOfTheWeek' })
  const dayOfTheMonth = useWatch({ control, name: 'dayOfTheMonth' })
  const serviceStartDate = useWatch({ control, name: 'serviceStartDate' })

  // Ensure arrays are aligned with frequency and interval transitions
  const currentFrequency = useMemo(() => parseInt(recurrenceFrequency, 10) || 1, [recurrenceFrequency])
  const freqFormData = useMemo(() => ({ eventOccurrence, dayOfTheWeek, dayOfTheMonth }), [eventOccurrence, dayOfTheWeek, dayOfTheMonth])

  useFrequencyArrayAdjustment(isScheduleTab, currentFrequency, freqFormData, setValue)
  useIntervalTransition(isScheduleTab, serviceInterval, currentFrequency, freqFormData, setValue, metaFiltersData)

  // Reset recurrence fields when priced service changes, preserve serviceStartDate
  useEffect(() => {
    if (!selectedPricedService) return
    const { recurrenceInterval, recurrencePer, recurrenceFrequency } = selectedPricedService || {}
    const intervalKey = recurrenceInterval && recurrencePer ? `${recurrenceInterval}-${recurrencePer}` : ''

    setValue('daysOfWeek', {}, { shouldValidate: false })
    setValue('eventInstanceType', createDraftDefaults.eventInstanceType, { shouldValidate: false })
    setValue('eventOccurrence', createDraftDefaults.eventOccurrence, { shouldValidate: false })
    setValue('dayOfTheWeek', createDraftDefaults.dayOfTheWeek, { shouldValidate: false })
    setValue('dayOfTheMonth', null, { shouldValidate: false })
    if (intervalKey) setValue('serviceInterval', intervalKey, { shouldValidate: false })
    if (recurrenceFrequency) setValue('recurrenceFrequency', recurrenceFrequency, { shouldValidate: false })
  }, [selectedPricedService, setValue])

  // Snapshot of recurrence fields for preview components
  const recurrenceFormData = useMemo(
    () => ({
      recurrenceFrequency,
      serviceInterval: serviceInterval || '',
      daysOfWeek: daysOfWeek || {},
      eventInstanceType: eventInstanceType ?? null,
      eventOccurrence: eventOccurrence ?? null,
      dayOfTheWeek: dayOfTheWeek ?? null,
      dayOfTheMonth: dayOfTheMonth ?? null,
    }),
    [recurrenceFrequency, serviceInterval, daysOfWeek, eventInstanceType, eventOccurrence, dayOfTheWeek, dayOfTheMonth]
  )

  const draftBaselineDefaults = useMemo(
    () => ({
      recurrenceFrequency: recurrenceFormData.recurrenceFrequency,
      serviceInterval: recurrenceFormData.serviceInterval,
      daysOfWeek: recurrenceFormData.daysOfWeek,
      eventInstanceType: recurrenceFormData.eventInstanceType,
      eventOccurrence: recurrenceFormData.eventOccurrence,
      dayOfTheWeek: recurrenceFormData.dayOfTheWeek,
      dayOfTheMonth: recurrenceFormData.dayOfTheMonth,
    }),
    [recurrenceFormData]
  )

  const serviceData = useMemo(
    () => ({
      actionType: selectedPricedService?.actionType,
      businessLine: selectedPricedService?.businessLine,
      businessLineType: selectedPricedService?.businessLineType,
    }),
    [selectedPricedService]
  )

  const existingEvents = useMemo(() => [], [])

  return {
    isScheduleTab,
    recurrenceOptions,
    recurrenceFormData,
    draftBaselineDefaults,
    serviceData,
    existingEvents,
    serviceStartDate,
  }
}
